import { updateDateTimes, updateAllDay } from 'owa-calendar-compose-form-actions';
import shouldHidePNTInfoBarForSingleProposer from 'owa-calendar-compose-form-store/lib/selectors/shouldHidePNTInfoBarForSingleProposer';
import { TimeChangeActionType } from 'owa-calendar-helpers-types';
import { InfoBarViewStates } from 'owa-calendar-info-bar/lib/utils/allViewStates';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { owaDate } from 'owa-datetime';

import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import {
    logProposedNewTimeInfobarClicked,
    logCurrentTimeInfobarClicked,
} from './instrumentationHelpers';
import {
    InfoBarMessageSource,
    InfoBarCustomAction,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

/**
 * One or more users have proposed a new time to meet
 * @param currentEvent
 * @param originalEvent
 */
export default function getFullComposeProposedTimeInfoBarMessageViewStates(
    currentEvent: CalendarEvent,
    originalEvent: CalendarEvent,
    formId: string
): InfoBarMessageViewState[] {
    let infoMesssages = [];

    const newTimeProposers = originalEvent.RequiredAttendees.concat(
        originalEvent.OptionalAttendees
    ).filter(attendee => attendee.ProposedStart);

    if (newTimeProposers.length > 0) {
        // Add current meeting time info bar if there is new proposer

        const meetingTimeCustomAction = <InfoBarCustomAction>{
            text: formatMeetingTimeSpan(
                // Format dates in the currently selected time zone
                owaDate(currentEvent.Start, originalEvent.Start),
                owaDate(currentEvent.End, originalEvent.End)
            ),
            action: () => {
                updateDateTimes(
                    formId,
                    {
                        // Restore dates to their former time zones
                        start: originalEvent.Start,
                        end: originalEvent.End,
                    },
                    TimeChangeActionType.FullComposeProposeNewTimeCurrentTime
                );
                updateAllDay(
                    originalEvent.IsAllDayEvent,
                    false /* isAllDayEventExplicitlySet */,
                    formId
                );
                logCurrentTimeInfobarClicked(InfoBarMessageSource.Compose);
            },
        };

        const curMeetingTimeInfoBarViewState = InfoBarViewStates.getInfobarViewStateForCurrentTime({
            source: InfoBarMessageSource.Compose,
            newTimeCustomAction: meetingTimeCustomAction,
        });

        infoMesssages.push(curMeetingTimeInfoBarViewState);
    }

    // single proposer
    if (newTimeProposers.length === 1 && shouldHidePNTInfoBarForSingleProposer(formId)) {
        // Hide proposed new time info bar in Compose form the setting is enabled and there is only one proposer
        return infoMesssages;
    } else {
        // Add propose new time info bar
        for (let i = 0; i < newTimeProposers.length; i++) {
            const attendee = newTimeProposers[i];

            const meetingTimeCustomAction = <InfoBarCustomAction>{
                text: formatMeetingTimeSpan(
                    // Format dates in the currently selected time zone if showing tz
                    owaDate(currentEvent.Start, attendee.ProposedStart),
                    owaDate(currentEvent.End, attendee.ProposedEnd)
                ),
                action: () => {
                    updateDateTimes(
                        formId,
                        {
                            // Preserve currently selected time zones when clicking a proposed time
                            start: owaDate(currentEvent.Start, attendee.ProposedStart),
                            end: owaDate(currentEvent.End, attendee.ProposedEnd),
                        },
                        TimeChangeActionType.FullComposeProposeNewTimeSuggestedTime
                    );
                    updateAllDay(
                        false /* isAllDayEvent */,
                        false /* isAllDayEventExplicitlySet */,
                        formId
                    );
                    logProposedNewTimeInfobarClicked(InfoBarMessageSource.Compose);
                },
            };
            const proposerInfoBarViewState = InfoBarViewStates.getInfobarViewStateForSingleProposedNewTime(
                {
                    newTimeProposers: [attendee],
                    source: InfoBarMessageSource.Compose,
                    newTimeCustomAction: meetingTimeCustomAction,
                }
            );

            infoMesssages.push(proposerInfoBarViewState);
        }

        return infoMesssages;
    }
}
